import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch } from "react-icons/fa";
import * as XLSX from "xlsx";
import PageHeader1 from "../../components/common/PageHeader1";
import toast from "react-hot-toast";

// Utility to format datetime (you can customize this function)
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString(); // Customize format if needed
};

const CouponReportDemo = () => {
  const token = localStorage.getItem("vedhvidhi-admin-token");

  const [coupons, setCoupons] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [totalCoupons, setTotalCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Delete dialog states
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  // Search and date filter state
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  // Fetch coupons with pagination and filters
  const fetchCoupons = async (page, limit, searchQuery = "", startDateStr = "", endDateStr = "") => {
    if (!token) {
      setError("Authorization token missing");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const apiPage = page + 1;
      let url = `${process.env.REACT_APP_BASEURL}/admin/coupon?page=${apiPage}&limit=${limit}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (startDateStr) url += `&startDate=${encodeURIComponent(startDateStr)}`;
      if (endDateStr) url += `&endDate=${encodeURIComponent(endDateStr)}`;

      const res = await fetch(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error("Failed to fetch coupons");

      const data = await res.json();

      const mappedRows = (data?.data || []).map((item, index) => ({
        ...item,
        id: item._id || item.id || index,
        sono: paginationModel.page * paginationModel.pageSize + index + 1,
        mobileNumber: item?.mobileNumber,
        astrologerName: item?.astrologerName,
        uniqueCodes:
          item?.type === "google_pay"
            ? item?.uniqueCodes?.map((uc) => uc.code).join(", ")
            : item?.code,
        couponCode: item?.couponCode,
        noOfUsersUse: item?.noOfUsersUse || item?.numCoupons || "N/A",
        usedCoupons: item?.usedCoupons || item?.usedBy?.length || "0",
        validFrom: item.validFrom
          ? new Date(item.validFrom).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          : "",

        validTo: item.validTo
          ? new Date(item.validTo).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          })
          : "",
        createdAt: item?.createdAt
          ? new Date(item?.createdAt).toLocaleString()
          : "",
        updatedAt: item?.updatedAt
          ? new Date(item?.updatedAt).toLocaleString()
          : "",
      }));

      setCoupons(mappedRows);
      setTotalCoupons(data?.paginationDetail?.totalDocs || 0);
    } catch (err) {
      setError(err.message || "Unknown error");
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }
    
    const { page, pageSize } = paginationModel;
    fetchCoupons(page ?? 0, pageSize ?? 10, "", appliedStartDate, appliedEndDate);
  }, [paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  const handleSearch = () => {
    if (!search.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchCoupons(0, 10, "", appliedStartDate, appliedEndDate);
      return;
    }
    setAppliedSearch(search);
    // Call API with only search parameter (no page/limit)
    fetchCoupons(0, 10, search, "", "");
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    
    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchCoupons(0, 10, "", appliedStartDate, appliedEndDate);
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    fetchCoupons(0, 10, "", startDate, endDate);
  };


  // Open delete dialog
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  // Close delete dialog
  const handleClose = () => setOpen(false);

  // Dummy delete handler: locally remove item
  const handleDelete = () => {
    setCoupons((prev) => prev.filter((c) => c.id !== selectedId));
    setOpen(false);
  };

  // Export coupons with filters
  const handleExport = async () => {
    try {
      setLoading(true);
      const apiPage = 1;
      let url = `${process.env.REACT_APP_BASEURL}/admin/coupon?page=${apiPage}&limit=10000`;
      if (appliedSearch) url += `&search=${encodeURIComponent(appliedSearch)}`;
      if (appliedStartDate) url += `&startDate=${encodeURIComponent(appliedStartDate)}`;
      if (appliedEndDate) url += `&endDate=${encodeURIComponent(appliedEndDate)}`;

      const res = await fetch(url, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error("Failed to export coupons");
      const data = await res.json();

      if (!data?.data || data.data.length === 0) {
        setError("No data to export");
        return;
      }

      const exportData = data.data.map((item, index) => ({
        SNo: index + 1,
        Category: item.category,
        Type: item.type,
        Code: item.code,
        "Max Usage": item.noOfUsersUse || item.numCoupons || "N/A",
        "Used Coupons": item.usedCoupons || item.usedBy?.length || "0",
        "Valid From": item.validFrom
          ? new Date(item.validFrom).toLocaleString()
          : "",
        "Valid To": item.validTo ? new Date(item.validTo).toLocaleString() : "",
        "Created At": item.createdAt ? new Date(item.createdAt).toLocaleString() : "",
        "Updated At": item.updatedAt ? new Date(item.updatedAt).toLocaleString() : "",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "CouponReport");
      XLSX.writeFile(workbook, `Coupon_Report_${new Date().toISOString().split("T")[0]}.xlsx`);
      setError(null);
    } catch (err) {
      setError(err.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setAppliedSearch("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setPaginationModel({ page: 0, pageSize: 10 });
    fetchCoupons(0, 10, "", "", "");
  };

  const columns = [
    {
      field: "sono",
      headerName: "S.No.",
      width: 80
    },
    { field: "category", headerName: "Coupon Category", width: 150 },
    { field: "type", headerName: "Coupon Type", width: 130 },
    { field: "uniqueCodes", headerName: " Google Coupon Code", width: 140 },
    { field: "couponCode", headerName: "Coupon Code", width: 140 },
    { field: "noOfUsersUse", headerName: "Max Usage Limit", width: 150 },
    { field: "usedCoupons", headerName: "Used Coupons", width: 140 },
    { field: "validTo", headerName: "Valid TO", width: 130 },
    { field: "validFrom", headerName: "Valid From", width: 130 },
    { field: "createdAt", headerName: "Created At", width: 130 },
    { field: "updatedAt", headerName: "Updated At", width: 130 },

    // {
    //   field: "actions",
    //   headerName: "Actions",
    //   width: 120,
    //   renderCell: (params) => (
    //     <Button variant="outlined" color="error" onClick={() => handleClickOpen(params.row.id)}>
    //       Delete
    //     </Button>
    //   ),
    // },
  ];

  // Calculate rowCount: use totalCoupons if available, otherwise use data length (for search)
  const rowCount = appliedSearch 
    ? (coupons?.length || 0)
    : (totalCoupons || 0);

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Coupon Usage Report" />
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, single line */}
                  <Box display="flex" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    <TextField
                      placeholder="Search..."
                      value={search}
                      onChange={handleSearchChange}
                      size="small"
                      sx={{ width: '250px' }}
                    />
                    <IconButton
                      onClick={handleSearch}
                      disabled={loading || !search.trim()}
                      sx={{ 
                        height: '40px',
                        width: '40px',
                        backgroundColor: 'primary.main',
                        color: 'white',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        '&:disabled': {
                          backgroundColor: 'action.disabledBackground',
                          color: 'action.disabled',
                        }
                      }}
                    >
                      <FaSearch />
                    </IconButton>
                    <TextField
                      type="date"
                      label="Start Date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <TextField
                      type="date"
                      label="End Date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      size="small"
                      sx={{ width: '180px' }}
                    />
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleApplyFilter}
                      disabled={loading}
                      sx={{ height: '40px' }}
                    >
                      {loading ? "Loading..." : "Apply"}
                    </Button>
                    <Button variant="contained" color="success" onClick={handleExport} disabled={loading} sx={{ height: '40px' }}>
                      Export
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="secondary" 
                      onClick={handleClearFilters}
                      sx={{ height: '40px' }}
                    >
                      Clear
                    </Button>
                  </Box>
                  
                  <Box sx={{ width: "100%", height: 600, position: "relative" }}>
                    {loading && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          zIndex: 1,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    )}

                    <DataGrid
                      columns={columns}
                      rows={coupons}
                      paginationMode={appliedSearch ? "client" : "server"}
                      rowCount={rowCount}
                      pageSizeOptions={[5, 10, 25, 50, 100]}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      loading={loading}
                      disableSelectionOnClick
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>Are you sure you want to delete this coupon?</DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CouponReportDemo;
